import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, Loader2, Send, Trash2, Calendar, Flag, User as UserIcon, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { photoUrl } from './CreateIssueModal';
import EvidenceButton from './sitewalk/EvidenceButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const IssueDetailModal = ({ issueId, currentUser, open, onClose, onChanged }) => {
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [uploadingComment, setUploadingComment] = useState(false);
  const [uploadingIssue, setUploadingIssue] = useState(false);
  const issuePhotoRef = useRef(null);
  const commentPhotoRef = useRef(null);

  useEffect(() => {
    if (!open || !issueId) return;
    setLoading(true);
    setComment('');
    setCommentPhotos([]);
    Promise.all([
      axios.get(`${API}/issues/${issueId}`),
      axios.get(`${API}/team`).catch(() => ({ data: [] })),
    ]).then(([iRes, tRes]) => {
      setIssue(iRes.data);
      setTeam(tRes.data || []);
    }).catch(() => toast.error('Failed to load issue')).finally(() => setLoading(false));
  }, [open, issueId]);

  if (!open) return null;

  const uploadTo = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(`${API}/projects/${issue.projectId}/issue-photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  };

  const addIssuePhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (issuePhotoRef.current) issuePhotoRef.current.value = '';
    if (!file) return;
    setUploadingIssue(true);
    try {
      const meta = await uploadTo(file);
      const res = await axios.post(`${API}/issues/${issueId}/attachments`, meta);
      setIssue((prev) => ({ ...prev, attachments: [...(prev.attachments || []), res.data] }));
      onChanged && onChanged();
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setUploadingIssue(false);
    }
  };

  const stageCommentPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (commentPhotoRef.current) commentPhotoRef.current.value = '';
    if (!file) return;
    setUploadingComment(true);
    try {
      const meta = await uploadTo(file);
      setCommentPhotos((prev) => [...prev, meta]);
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setUploadingComment(false);
    }
  };

  const patch = async (payload) => {
    try {
      const res = await axios.patch(`${API}/issues/${issueId}`, payload);
      setIssue(res.data);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const postComment = async () => {
    if (!comment.trim() && commentPhotos.length === 0) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/issues/${issueId}/comments`, { text: comment.trim(), attachments: commentPhotos });
      setIssue((prev) => ({ ...prev, comments: [...(prev.comments || []), res.data] }));
      setComment('');
      setCommentPhotos([]);
      onChanged && onChanged();
    } catch (e) {
      toast.error('Failed to add comment');
    } finally {
      setPosting(false);
    }
  };

  const removeIssue = async () => {
    if (!window.confirm('Delete this issue permanently?')) return;
    try {
      await axios.delete(`${API}/issues/${issueId}`);
      toast.success('Issue deleted');
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed (CompanyAdmin only)');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="issue-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {loading || !issue ? (
          <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : (
          <>
            <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
              <div className="pr-4">
                <input
                  value={issue.title}
                  onChange={(e) => setIssue({ ...issue, title: e.target.value })}
                  onBlur={(e) => e.target.value.trim() && e.target.value !== issue.title && patch({ title: e.target.value.trim() })}
                  className="text-lg font-semibold text-slate-900 w-full border-b border-transparent hover:border-slate-200 focus:border-amber-400 outline-none"
                  data-testid="issue-detail-title"
                />
                <p className="text-xs text-slate-400 mt-1">Created by {issue.createdByName || 'user'} · {new Date(issue.createdAt).toLocaleString()}</p>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="issue-detail-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                  <select value={issue.status} onChange={(e) => patch({ status: e.target.value })} className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="issue-detail-status">
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1"><UserIcon className="w-3 h-3 inline" /> Assignee</label>
                  <select
                    value={issue.assignedTo || ''}
                    onChange={(e) => patch(e.target.value ? { assignedTo: e.target.value } : { clearAssignee: true })}
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm"
                    data-testid="issue-detail-assignee"
                  >
                    <option value="">Unassigned</option>
                    {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1"><Flag className="w-3 h-3 inline" /> Priority</label>
                  <select value={issue.priority || 'medium'} onChange={(e) => patch({ priority: e.target.value })} className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="issue-detail-priority">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1"><Calendar className="w-3 h-3 inline" /> Due</label>
                  <input type="date" value={issue.dueDate ? issue.dueDate.slice(0, 10) : ''} onChange={(e) => patch({ dueDate: e.target.value || null })} className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="issue-detail-due" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                <textarea
                  value={issue.description || ''}
                  onChange={(e) => setIssue({ ...issue, description: e.target.value })}
                  onBlur={(e) => patch({ description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm whitespace-pre-wrap"
                  data-testid="issue-detail-desc"
                />
              </div>

              {issue.sourceJobId && (
                <p className="text-xs text-slate-500">Source job: <span className="font-mono">{issue.sourceJobId.slice(0, 8)}</span></p>
              )}

              {issue.projectId && (
                <div data-testid="issue-360-evidence">
                  <EvidenceButton projectId={issue.projectId} kind="issue" id={issue.id} label="Open 360 Evidence" />
                </div>
              )}

              {/* Photo evidence */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Photo Evidence</label>
                <input ref={issuePhotoRef} type="file" accept="image/*" className="hidden" onChange={addIssuePhoto} data-testid="issue-detail-photo-input" />
                <div className="flex items-center gap-2 flex-wrap" data-testid="issue-detail-attachments">
                  {(issue.attachments || []).map((a, i) => (
                    <a key={i} href={photoUrl(a.s3Key)} target="_blank" rel="noreferrer">
                      <img src={photoUrl(a.s3Key)} alt={a.fileName} className="w-16 h-16 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-amber-400" data-testid="issue-detail-photo" />
                    </a>
                  ))}
                  <button onClick={() => issuePhotoRef.current?.click()} disabled={uploadingIssue} className="w-16 h-16 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-50 disabled:opacity-60" data-testid="issue-detail-add-photo">
                    {uploadingIssue ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Comments */}
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">Comments ({(issue.comments || []).length})</h4>
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="issue-comments">
                  {(issue.comments || []).length === 0 && <p className="text-xs text-slate-400">No comments yet.</p>}
                  {(issue.comments || []).map((c) => (
                    <div key={c.id} className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                      <div className="flex justify-between">
                        <span className="text-xs font-semibold text-slate-700">{c.userName || 'User'}</span>
                        <span className="text-[10px] text-slate-400">{new Date(c.createdAt).toLocaleString()}</span>
                      </div>
                      {c.text && <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.text}</p>}
                      {(c.attachments || []).length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-1">
                          {c.attachments.map((a, i) => (
                            <a key={i} href={photoUrl(a.s3Key)} target="_blank" rel="noreferrer">
                              <img src={photoUrl(a.s3Key)} alt={a.fileName} className="w-14 h-14 object-cover rounded border border-slate-200" data-testid="comment-photo" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {commentPhotos.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2" data-testid="staged-comment-photos">
                    {commentPhotos.map((a, i) => (
                      <img key={i} src={photoUrl(a.s3Key)} alt={a.fileName} className="w-12 h-12 object-cover rounded border border-amber-300" />
                    ))}
                  </div>
                )}
                <input ref={commentPhotoRef} type="file" accept="image/*" className="hidden" onChange={stageCommentPhoto} data-testid="comment-photo-input" />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => commentPhotoRef.current?.click()} disabled={uploadingComment} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 disabled:opacity-60" data-testid="comment-add-photo" title="Attach photo">
                    {uploadingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  </button>
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && postComment()}
                    placeholder="Add a comment…"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="issue-comment-input"
                  />
                  <button onClick={postComment} disabled={posting} className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60" data-testid="issue-comment-submit">
                    {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center px-5 py-4 border-t border-slate-200">
              {currentUser?.role === 'CompanyAdmin' ? (
                <button onClick={removeIssue} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="issue-delete-btn"><Trash2 className="w-4 h-4" /> Delete</button>
              ) : <span />}
              <button onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IssueDetailModal;
